import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { BaseEdge, EdgeLabelRenderer, useReactFlow, getSmoothStepPath } from 'reactflow'

import type { ReactElement } from 'react'
import type { EdgeProps } from 'reactflow'
import type { ITask } from '~/types/app-setup'
import type { ITodolistTemplate } from '~/types/todolist-template'

import { AUTHORIZATIONS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { keys as todolistTemplateKeys, useUpdateTodolistTemplateMutation } from '~/queries/todolist-templates'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const TodolistTemplateButtonEdge = ({
  data,
  id,
  markerEnd,
  source,
  sourcePosition,
  sourceX,
  sourceY,
  style = {},
  target,
  targetPosition,
  targetX,
  targetY,
}: EdgeProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { todolistTemplate } = data as { todolistTemplate: ITodolistTemplate }

  const isActionAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.TODOLIST.WRITE])

  const { getNodes, setEdges } = useReactFlow<ITask>()

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourcePosition,
    sourceX,
    sourceY,
    targetPosition,
    targetX,
    targetY,
  })

  /** Queries & Mutations */

  const { mutateAsync: updateTodolistTemplate } = useUpdateTodolistTemplateMutation()

  /** Handlers */

  const handleEdgeClick = async () => {
    const nodes = getNodes()
    const targetNode = nodes.find(({ id }) => id === target)

    if (todolistTemplate && targetNode) {
      try {
        const newTasks = todolistTemplate.tasks.map((t) =>
          t.template_task_id === target
            ? { ...t, previous_steps: t.previous_steps.filter((step) => step !== source) }
            : t
        )

        await updateTodolistTemplate({
          data: {
            tasks: newTasks,
            template: todolistTemplate.template,
          },
          templateId: todolistTemplate.template.template_id,
        })

        await queryClient.invalidateQueries({
          queryKey: todolistTemplateKeys.details(todolistTemplate.template.template_id),
        })

        setEdges((edges) => edges.filter((edge) => edge.id !== id))
      } catch (e) {
        captureException(e)
        displayToast('Tasks cannot be unlinked!', 'error')
      }
    }
  }

  return (
    <>
      <BaseEdge markerEnd={markerEnd} path={edgePath} style={style} />
      {isActionAuthorized ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              fontSize: 12,
              // everything inside EdgeLabelRenderer has no pointer events by default
              // if you have an interactive element, set pointer-events: all
              pointerEvents: 'all',
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            <button className="todolist-template-button-edge-component" onClick={handleEdgeClick}>
              ×
            </button>
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  )
}

export default TodolistTemplateButtonEdge
